import { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { Workflow } from '../../types';
import { formatCurrency, formatNumber, formatSmallNumber } from '../../utils/formatting';

interface WorkflowDetailsProps {
  workflows: Workflow[];
}

interface WorkflowRowProps {
  workflow: Workflow;
}

function WorkflowRow({ workflow }: WorkflowRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'none' : undefined } }}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" sx={{ p: 0.5 }}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {workflow.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {workflow.description}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">
            {formatNumber(workflow.monthly_count)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="caption" color="text.secondary">
            {workflow.cost_per_run_cents > 0
              ? `${workflow.cost_per_run_cents.toFixed(6)}¢ ${workflow.cost_per_unit_label || ''}`
              : '—'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={500}>
            {formatCurrency(workflow.total_cost)}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Level 3: Service breakdown */}
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, bgcolor: 'action.hover' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 4 }}>
              <Typography variant="caption" fontWeight={500} color="text.secondary" gutterBottom>
                AWS Service Breakdown
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>Service</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>Count</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workflow.services.map((service, idx) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {service.service_label}
                        </Typography>
                        {service.resource_name && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem' }}>
                            {service.resource_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {service.count > 0 ? formatNumber(service.count) : '-'}
                          {service.count_unit && (
                            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                              {service.count_unit}
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <Typography variant="caption" fontWeight={500}>
                          {formatSmallNumber(service.cost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function WorkflowDetails({ workflows }: WorkflowDetailsProps) {
  const [showDetails, setShowDetails] = useState(true);

  if (!workflows || workflows.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
      <Box
        onClick={() => setShowDetails(!showDetails)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: showDetails ? 2 : 0,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Workflow Details
        </Typography>
        <Chip
          label={`${workflows.length} workflows`}
          size="small"
          variant="outlined"
          sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
        />
        <Tooltip title={showDetails ? 'Hide details' : 'Show workflow breakdown'}>
          <IconButton size="small">
            {showDetails ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={showDetails}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Workflow</TableCell>
                <TableCell align="right">Monthly Count</TableCell>
                <TableCell align="right">Cost/Run</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workflows.map((workflow) => (
                <WorkflowRow key={workflow.name} workflow={workflow} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

// Export with old name for backwards compatibility
export { WorkflowDetails as OperationDetails };
